/**
 * Navigation controller — the bridge between the XState machine and both
 * the 3D rig tree and the DOM overlay tree.
 *
 * Plan 05-01 Task 1 restructure. Previously NavController was mounted INSIDE
 * the R3F <Canvas> and returned a rig element. That worked for Phase 3 where
 * no DOM component needed to dispatch machine events, but Plan 05-01 introduces
 * <BoardingOverlay /> (DOM, in UIShell) and Plan 05-02 introduces
 * <ReturnToStreetcarButton /> (DOM, in UIShell) — both MUST share the same
 * `send` function as the Canvas-scope StreetcarRig (END_OF_LINE dispatch).
 *
 * Solution: NavController is hoisted to App root. It renders a NavSendContext
 * provider that wraps BOTH the Canvas tree and the UIShell tree via its
 * children prop. Rig selection moved into a separate <NavRig /> component
 * (live inside the Canvas) that reads navState from useNavStore (already
 * kept in sync by this controller via setNavState).
 *
 * Responsibilities:
 *   1. Subscribe to navMachine via @xstate/react useMachine
 *   2. Log every transition to console + window CustomEvent (SC #5 proof)
 *   3. Mirror the current state into useNavStore.navState so both <NavRig>
 *      (Canvas) and DOM overlays can react
 *   4. Expose send() globally under `?debug=1` for DebugHUD / browser-console
 *      smoke tests
 *   5. Provide `send` via <NavSendContext.Provider> so any descendant —
 *      Canvas-scope or DOM-scope — can call useNavSend()
 *
 * Phase 3's auto-advance setTimeout cascade is GONE. The boarding flow is now:
 *   - User sees <BoardingOverlay /> (Plan 05-01 Task 3)
 *   - Click "Board the streetcar" → send({type:'START'})
 *   - Machine enters `boarding`; XState `after: 1500` auto-advances to
 *     `streetcarRiding` (matches the streetcar-bell beat, satisfies the
 *     browser autoplay-gesture requirement for Plan 05-05 audio)
 *   - StreetcarRig rides the spline; fires END_OF_LINE via useNavSend at t=1
 *   - arrivalCinematic runs; ArrivalCinematic (Plan 05-02) dispatches ARRIVAL_DONE
 */

import { useMachine } from '@xstate/react';
import { useEffect, useRef, type ReactNode } from 'react';
import { navMachine } from './machine';
import { NavSendContext } from './NavContext';
import { logTransition } from './transitionLogger';
import { useNavStore, type NavStateValue } from '../store/useNavStore';

const VALID_STATES: NavStateValue[] = [
  'idle',
  'boarding',
  'streetcarRiding',
  'arrivalCinematic',
  'freeWalk',
  'guidedTour',
  'photoCompare',
];

function asNavState(v: unknown): NavStateValue {
  return VALID_STATES.includes(v as NavStateValue) ? (v as NavStateValue) : 'idle';
}

export function NavController({ children }: { children?: ReactNode } = {}) {
  const [state, send] = useMachine(navMachine);
  const setNavStateStore = useNavStore((s) => s.setNavState);
  const prevState = useRef<string>('idle');
  const current = asNavState(state.value);

  // Log every transition to console + window event (SC #5 proof artifact)
  useEffect(() => {
    if (prevState.current !== current) {
      logTransition(prevState.current, current);
      prevState.current = current;
    }
    setNavStateStore(current);
  }, [current, setNavStateStore]);

  // Expose send() globally for DebugHUD + browser-console smoke tests (?debug=1 only)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('debug') !== '1') return;
    (window as unknown as { __navSend?: typeof send }).__navSend = send;
    return () => {
      delete (window as unknown as { __navSend?: typeof send }).__navSend;
    };
  }, [send]);

  return (
    <NavSendContext.Provider value={send as (event: { type: string }) => void}>
      {children}
    </NavSendContext.Provider>
  );
}
